import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import {
  GuardianLinkEndReasonSchema,
  GuardianLinkStatusSchema,
} from '../guardian-link-status/guardian-link-status';
import { GuardianLabelSchema } from '../guardian-label/guardian-label';

/**
 * A ward, as her guardian reads her — the mirror of `GuardianLinkSchema`, and a
 * separate schema for the reason stated there.
 *
 * `wardLabel` is what he wrote when he redeemed her code, and it is the whole
 * reason this payload is usable: a wali holding three wards would otherwise be
 * looking at three uuids. Her own label for *him* is not here — it is hers.
 *
 * **This is the guardian's entire view of his ward in P24a.** Her conversations
 * arrive in P24b, on their own routes, and that is where the question of what he
 * may see *of the person she is talking to* gets answered through the visibility
 * resolver. Nothing here reads a profile answer.
 */
export const WardLinkSchema = z
  .object({
    id: UuidSchema,
    wardUserId: UuidSchema,
    /** What he called her when he redeemed the code. His, and never shown to her. */
    wardLabel: GuardianLabelSchema,
    status: GuardianLinkStatusSchema,
    linkedAt: z.iso.datetime(),
    endedAt: z.iso.datetime().nullable(),
    endedReason: GuardianLinkEndReasonSchema.nullable(),
    createdAt: z.iso.datetime(),
  })
  .strict()
  .refine((link) => (link.status === 'ended') === (link.endedAt !== null), {
    message: 'An ended link records when it ended, and a live one records nothing.',
    path: ['endedAt'],
  });

export type WardLink = z.infer<typeof WardLinkSchema>;
